import React, { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from '../config/api';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = ['open', 'closed'];

const STATUS_COLOR = {
  open:   '#3fb950',
  closed: '#8b949e',
};

const HedgeOpportunities = () => {
  const { user } = useAuth();

  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [symbolFilter, setSymbolFilter]     = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [statusFilter, setStatusFilter]     = useState('');
  // Client-side numeric filters: keep rows whose diff is strictly greater than
  // the entered value. Empty string = no filter.
  const [openDiffMin, setOpenDiffMin]       = useState('');
  const [obOpenDiffMin, setObOpenDiffMin]   = useState('');
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState('50');

  const authHeader = { 'x-user-id': user?.sub };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        symbol:        symbolFilter,
        exchange:      exchangeFilter,
        status:        statusFilter,
        openDiffMin:   openDiffMin,
        obOpenDiffMin: obOpenDiffMin,
        page,
        pageSize,
      });
      const res  = await fetch(`${API_BASE_URL}/api/admin/hedge-opportunities?${params}`, { headers: authHeader });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load');
      setRows(data.data);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [symbolFilter, exchangeFilter, statusFilter, openDiffMin, obOpenDiffMin, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce refetches so typing into a filter (especially the expensive
  // OB-diff scan) fires a single request once input settles, instead of one
  // request per keystroke.
  useEffect(() => {
    const id = setTimeout(fetchRows, 350);
    return () => clearTimeout(id);
  }, [fetchRows]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handlePageSizeChange = (val) => {
    setPageSize(val);
    setPage(1);
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // Same as fmtDate but with 2 decimal points of ms (hundredths), e.g. 2026-06-04 09:45:23.69
  const fmtDateMs = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    const cs = pad(Math.floor(d.getMilliseconds() / 10));
    return `${fmtDate(ts)}.${cs}`;
  };

  // Duration between opened and closed, in seconds with 2 decimals.
  const fmtDuration = (opened, closed) => {
    if (!opened || !closed) return '—';
    const diff = (new Date(closed).getTime() - new Date(opened).getTime()) / 1000;
    if (Number.isNaN(diff)) return '—';
    return `${diff.toFixed(2)} s`;
  };

  const fmtNum = (v, digits = 6) => {
    if (v == null) return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return n.toLocaleString('en-US', { maximumFractionDigits: digits });
  };

  // Why a Redis-derived OB value came back empty (see adminController's
  // fetchObAtTimestamp `reason` codes).
  const OB_REASON_LABEL = {
    missing_exchange: '<no exchange>',
    missing_market:   '<no market>',
    missing_symbol:   '<no symbol>',
    missing_timestamp:'<no timestamp>',
    no_stream:        '<no stream>',
    too_old:          '<too old>',
    incomplete_entry: '<incomplete>',
    redis_error:      '<redis error>',
    not_closed:       '—',
  };

  // Like fmtNum, but falls back to a reason label (instead of a bare "-")
  // when the value is missing because of a known Redis lookup failure.
  const fmtNumR = (v, reason, digits = 6) => {
    if (v != null) return fmtNum(v, digits);
    if (!reason) return '—';
    return OB_REASON_LABEL[reason] || `<${reason}>`;
  };

  const fmtPct = (v) => {
    if (v == null) return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return `${n.toFixed(4)} %`;
  };

  const inputStyle = {
    padding: '6px 10px', background: '#0d1117', border: '1px solid #30363d',
    borderRadius: '6px', color: '#e6edf3', fontSize: '13px', outline: 'none',
  };

  const thStyle = {
    padding: '8px 10px', fontWeight: 500, color: '#8b949e',
    borderBottom: '1px solid #30363d', textAlign: 'left', whiteSpace: 'nowrap',
  };

  const tdStyle = { padding: '7px 10px', borderBottom: '1px solid #21262d', fontSize: '13px', whiteSpace: 'nowrap' };
  const mutedTd = { ...tdStyle, color: '#8b949e' };
  const monoTd  = { ...tdStyle, fontFamily: "'Courier New', Courier, monospace" };

  // Symbol cell: name on top, id muted below.
  const NameId = ({ name, id }) => (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
      <span>{name || '—'}</span>
      <span style={{ color: '#8b949e', fontSize: '11px' }}>id: {id ?? '—'}</span>
    </div>
  );

  // Exchange-side cell: "<name>(<id>)" on top, position below.
  const ExchangeSide = ({ name, id, position }) => (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
      <span>{name || '—'}({id ?? '—'})</span>
      <span style={{ color: '#8b949e', fontSize: '11px' }}>{position || '—'}</span>
    </div>
  );

  const GREEN = '#3fb950';

  // Shared width so header labels line up with the value cells below them.
  const priceColW = '90px';

  // A header label cell for the PRICES column.
  const PriceLabel = ({ children }) => (
    <span style={{ display: 'inline-block', minWidth: priceColW, fontWeight: 500, color: '#8b949e', fontSize: '11px' }}>
      {children}
    </span>
  );

  // A value cell under the PRICES column.
  const PriceVal = ({ color, children }) => (
    <span style={{ display: 'inline-block', minWidth: priceColW, fontFamily: "'Courier New', Courier, monospace", color }}>
      {children}
    </span>
  );

  // Two-line stacked header label (one column, two rows that line up with
  // the two-row prices column).
  const TwoLineHead = ({ top, bottom }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '11px', color: '#8b949e' }}>
      <span>{top}</span>
      <span>{bottom}</span>
    </div>
  );

  // Two-line stacked monospace value cell.
  const TwoLineVal = ({ top, bottom, topColor, bottomColor }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontFamily: "'Courier New', Courier, monospace" }}>
      <span style={{ color: topColor }}>{top}</span>
      <span style={{ color: bottomColor }}>{bottom}</span>
    </div>
  );

  const COLSPAN = 17;

  return (
    <div style={{ padding: '24px', color: '#e6edf3', fontFamily: 'inherit' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>Hedge Opportunities</h1>
        <p style={{ color: '#8b949e', fontSize: '13px', margin: 0 }}>
          hedge_price_medium_speed_service_opportunities table — {total} rows total
        </p>
      </div>

      {/* Filters */}
      <form onSubmit={handleFilterSubmit}
        style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          style={{ ...inputStyle, width: '180px' }}
          placeholder="Symbol name..."
          value={symbolFilter}
          onChange={e => setSymbolFilter(e.target.value)}
        />
        <input
          style={{ ...inputStyle, width: '180px' }}
          placeholder="Exchange name (either side)..."
          value={exchangeFilter}
          onChange={e => setExchangeFilter(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#8b949e' }}>
          open diff &gt;
          <input
            type="number"
            step="any"
            style={{ ...inputStyle, width: '90px' }}
            placeholder="any"
            value={openDiffMin}
            onChange={e => { setOpenDiffMin(e.target.value); setPage(1); }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#8b949e' }}>
          OB open diff &gt;
          <input
            type="number"
            step="any"
            style={{ ...inputStyle, width: '90px' }}
            placeholder="any"
            value={obOpenDiffMin}
            onChange={e => { setObOpenDiffMin(e.target.value); setPage(1); }}
          />
        </div>
        <button type="submit"
          style={{ ...inputStyle, cursor: 'pointer', background: '#21262d', border: '1px solid #30363d' }}>
          Filter
        </button>
        {(symbolFilter || exchangeFilter || statusFilter || openDiffMin || obOpenDiffMin) && (
          <button type="button"
            onClick={() => { setSymbolFilter(''); setExchangeFilter(''); setStatusFilter(''); setOpenDiffMin(''); setObOpenDiffMin(''); setPage(1); }}
            style={{ ...inputStyle, cursor: 'pointer', background: 'none', color: '#8b949e', border: '1px solid #30363d' }}>
            Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#8b949e' }}>
          Rows per page:
          {['50', '100', 'all'].map(v => (
            <button key={v} type="button"
              onClick={() => handlePageSizeChange(v)}
              style={{
                padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                background: pageSize === v ? '#58a6ff' : '#21262d',
                color:      pageSize === v ? '#0a0b0f' : '#e6edf3',
                border:     '1px solid #30363d',
              }}>
              {v}
            </button>
          ))}
        </div>
      </form>

      {/* Table */}
      {loading ? (
        <div style={{ color: '#8b949e' }}>Loading...</div>
      ) : error ? (
        <div style={{ color: '#f85149' }}>{error}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '1200px' }}>
              <thead>
                <tr>
                  <th style={thStyle}><TwoLineHead top="OPPORT ID" bottom="SERVICE ID" /></th>
                  <th style={thStyle}>STATUS</th>
                  <th style={thStyle}>SYMBOL</th>
                  <th style={thStyle}>EX1 (short)</th>
                  <th style={thStyle}>EX2 (long)</th>
                  <th style={thStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <div style={{ display: 'flex', gap: '14px' }}>
                        <PriceLabel>Ex1 open</PriceLabel>
                        <PriceLabel>Ex2 open</PriceLabel>
                        <PriceLabel>open diff</PriceLabel>
                        <PriceLabel>after open diff</PriceLabel>
                      </div>
                      <div style={{ display: 'flex', gap: '14px' }}>
                        <PriceLabel>Ex1 close</PriceLabel>
                        <PriceLabel>Ex2 close</PriceLabel>
                        <PriceLabel>close diff</PriceLabel>
                      </div>
                    </div>
                  </th>
                  <th style={thStyle}><TwoLineHead top="ex1 best bid/ask" bottom="ex2 best bid/ask" /></th>
                  <th style={thStyle}><TwoLineHead top="ex1 open OB bid" bottom="ex2 open OB ask" /></th>
                  <th style={thStyle}><TwoLineHead top="ex1 close OB ask" bottom="ex2 close OB bid" /></th>
                  <th style={thStyle}><TwoLineHead top="OB open diff" bottom="OB close diff" /></th>
                  <th style={thStyle}>QTY AVG100</th>
                  <th style={thStyle}>VOL USDT AVG100</th>
                  <th style={thStyle}>QTY DTM0.1%</th>
                  <th style={thStyle}>VOL USDT DTM0.1%</th>
                  <th style={thStyle}>OPENED</th>
                  <th style={thStyle}>DURATION</th>
                  <th style={thStyle}>LAST UPDATE</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLSPAN} style={{ ...tdStyle, textAlign: 'center', color: '#8b949e' }}>No rows found</td>
                  </tr>
                ) : rows.map(row => (
                  <tr key={row.opport_id}
                    onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={mutedTd}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <span>{row.opport_id}</span>
                        <span>{row.hedge_price_medium_speed_service_id ?? '—'}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: STATUS_COLOR[row.status] || '#8b949e' }}>
                        {row.status || '—'}
                      </span>
                    </td>
                    <td style={tdStyle}><NameId name={row.symbol_name} id={row.symbol_id} /></td>
                    <td style={tdStyle}><ExchangeSide name={row.ex1_name} id={row.ex1_id} position={row.ex1_position} /></td>
                    <td style={tdStyle}><ExchangeSide name={row.ex2_name} id={row.ex2_id} position={row.ex2_position} /></td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div style={{ display: 'flex', gap: '14px' }}>
                          <PriceVal>{fmtNum(row.ex1_price_open)}</PriceVal>
                          <PriceVal>{fmtNum(row.ex2_price_open)}</PriceVal>
                          <PriceVal color={GREEN}>{fmtPct(row.price_open_diff_percent)}</PriceVal>
                          <PriceVal
                            color={Number(row.price_after_open_diff_percent) > Number(row.price_open_diff_percent) ? GREEN : undefined}>
                            {fmtPct(row.price_after_open_diff_percent)}
                          </PriceVal>
                        </div>
                        <div style={{ display: 'flex', gap: '14px' }}>
                          <PriceVal>{fmtNum(row.ex1_price_close)}</PriceVal>
                          <PriceVal>{fmtNum(row.ex2_price_close)}</PriceVal>
                          <PriceVal>{fmtPct(row.price_close_diff_percent)}</PriceVal>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontFamily: "'Courier New', Courier, monospace", fontSize: '11px' }}>
                        <span>{fmtNumR(row.ex1_best_bid, row.ex1_open_ob_reason)} / {fmtNumR(row.ex1_best_ask, row.ex1_open_ob_reason)}</span>
                        <span>{fmtNumR(row.ex2_best_bid, row.ex2_open_ob_reason)} / {fmtNumR(row.ex2_best_ask, row.ex2_open_ob_reason)}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <TwoLineVal
                        top={fmtNumR(row.ex1_open_ob_bid, row.ex1_open_ob_reason)}
                        bottom={fmtNumR(row.ex2_open_ob_ask, row.ex2_open_ob_reason)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <TwoLineVal
                        top={fmtNumR(row.ex1_close_ob_ask, row.ex1_close_ob_reason)}
                        bottom={fmtNumR(row.ex2_close_ob_bid, row.ex2_close_ob_reason)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <TwoLineVal
                        top={fmtPct(row.ob_open_diff_percent)}
                        bottom={fmtPct(row.ob_close_diff_percent)}
                        topColor={Number(row.ob_open_diff_percent) > 0 ? GREEN : undefined}
                        bottomColor={Number(row.ob_close_diff_percent) > 0 ? GREEN : undefined}
                      />
                    </td>
                    <td style={monoTd}>{fmtNum(row.rcmd_qty_avg_100trds)}</td>
                    <td style={monoTd}>{fmtNum(row.rcmd_volume_usdt_avg_100trds, 2)}</td>
                    <td style={monoTd}>{fmtNum(row.rcmd_qty_dtm01prct)}</td>
                    <td style={monoTd}>{fmtNum(row.rcmd_volume_usdt_dtm01prct, 2)}</td>
                    <td style={mutedTd}>{fmtDateMs(row.opened_timestamp)}</td>
                    <td style={mutedTd}>{fmtDuration(row.opened_timestamp, row.closed_timestamp)}</td>
                    <td style={mutedTd}>{fmtDate(row.last_update_timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageSize !== 'all' && totalPages > 1 && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '16px', fontSize: '13px', color: '#8b949e' }}>
              <button
                onClick={() => setPage(1)} disabled={page === 1}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>
                «
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>
                ‹
              </button>
              <span style={{ padding: '0 8px' }}>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>
                ›
              </button>
              <button
                onClick={() => setPage(totalPages)} disabled={page === totalPages}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>
                »
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HedgeOpportunities;
